/**
 * The account **Invite** dialog (M8-P2 §6.10), rebuilt in round 9j from
 * `[cap:p1p2-invite-dialog]` rather than approximated (P-039). Opened from the top bar's
 * *Invite members*, from the workspace home's **Invite / N** and from every *Invite* button in
 * the admin.
 *
 * Measured, at 1440 × 900: modal **620 × 471** at (410, 215), radius **16**, shadow
 * `rgba(0,0,0,.3) 0 15px 50px`; content column **572** at x 434; the label row *Invite with
 * email* at y 285 (33 tall); one bordered block 572 × 252 at y 325 holding the e-mail input
 * **426 × 38** beside a **120 × 32** role dropdown, a 554 × 1 divider at y 391 and the custom
 * message textarea **554 × 76** at y 400; the footer buttons are **40** tall — *Copy invite
 * link* 170 ending at x 886, *Send invite* 112 at x 894 → 1006.
 *
 * Two deliberate differences from monday, both stated in the spec:
 *   - monday's three "Add context" chips are an onboarding nudge, not a message, and are
 *     absent (§3.2). The textarea in their place is F3's `invite({message})`, which monday
 *     renders in exactly that box.
 *   - **Copy invite link** creates a real single-use invitation for the first address rather
 *     than a shareable join-anybody URL, and says so.
 */
import { useMemo, useState } from 'react';
import { useOverlayDismiss } from '@elizmonday/ui';
import type { AccountRole, AdminSource, InviteResult } from '../admin/types';
import '../admin/admin.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/**
 * monday's four. `superadmin` is ours and is never invited by e-mail (T-9f): it is granted by
 * `pnpm auth:bootstrap-admin --role superadmin` or by another superadmin in the directory, so
 * the dialog does not offer it and the service refuses it.
 */
const ROLE_LABELS: Partial<Record<AccountRole, string>> = {
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
  guest: 'Guest',
};

/** The same split the service does (`services/invitations.ts#parseAddressList`). */
export function splitAddresses(raw: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const rawPart of raw.split(/[\s,;]+/)) {
    const part = rawPart.trim().replace(/^.*<|>.*$/g, '');
    if (!part) continue;
    const address = part.toLowerCase();
    if (seen.has(address)) continue;
    seen.add(address);
    if (EMAIL_RE.test(address)) valid.push(address);
    else invalid.push(part);
  }
  return { valid, invalid };
}

export function InviteDialog(props: {
  source: AdminSource;
  productName: string;
  /** A member inviter may never choose Admin (F8 §5.3); the option is hidden, not disabled. */
  canInviteAdmins: boolean;
  onClose: () => void;
}): React.ReactElement {
  const [raw, setRaw] = useState('');
  const [draft, setDraft] = useState('');
  const [role, setRole] = useState<AccountRole>('member');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * P-013. This dialog ignored Escape entirely — the sweep typed a character into the address
   * field, pressed Escape and found both the character and the dialog still there. Escape now
   * closes it and focus returns to the *Invite* button that opened it, like every other
   * overlay in the product.
   */
  useOverlayDismiss({ onClose: props.onClose });

  const parsed = useMemo(() => splitAddresses(raw), [raw]);
  const roles: AccountRole[] = props.canInviteAdmins
    ? ['admin', 'member', 'viewer', 'guest']
    : ['member', 'viewer', 'guest'];

  const commitDraft = (): void => {
    if (draft.trim().length === 0) return;
    setRaw((current) => `${current} ${draft}`.trim());
    setDraft('');
  };

  const send = async (): Promise<void> => {
    try {
      const answer = await props.source.invite({
        paste: `${raw} ${draft}`.trim(),
        role,
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      setResult(answer);
      setError(null);
      setRaw('');
      setDraft('');
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const ready = splitAddresses(`${raw} ${draft}`).valid.length > 0;

  return (
    <div className="ez-invite" role="dialog" aria-label="Invite" data-testid="invite-dialog">
      <div className="ez-invite__content">
        <div className="ez-invite__header">
          <h2 className="ez-invite__title">Invite to {props.productName}</h2>
          <button
            type="button"
            className="ez-invite__close"
            aria-label="Close"
            data-testid="invite-close"
            onClick={props.onClose}
          >
            ×
          </button>
        </div>

        <div className="ez-invite__label">Invite with email</div>

        {/* monday's one bordered block: addresses + role, a divider, then the message. */}
        <div className="ez-invite__box">
          <div className="ez-invite__inputs">
            <div className="ez-invite__tokens" data-testid="invite-tokens">
              {parsed.valid.map((address) => (
                <span key={address} className="ez-invite__token">
                  {address}
                </span>
              ))}
              {parsed.invalid.map((address) => (
                <span
                  key={address}
                  className="ez-invite__token ez-invite__token--invalid"
                  title="That does not look like an e-mail address."
                >
                  {address}
                </span>
              ))}
              <input
                aria-label="Invite with email"
                placeholder="name@example.com, name@example.com..."
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commitDraft}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
                    event.preventDefault();
                    commitDraft();
                  }
                }}
                onPaste={(event) => {
                  const text = event.clipboardData.getData('text');
                  if (text.length > 0) {
                    event.preventDefault();
                    setRaw((current) => `${current} ${text}`.trim());
                  }
                }}
                data-testid="invite-input"
              />
            </div>

            <select
              id="ez-invite-role"
              className="ez-invite__role"
              aria-label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value as AccountRole)}
              data-testid="invite-role"
            >
              {roles.map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABELS[option]}
                </option>
              ))}
            </select>
          </div>

          <div className="ez-invite__divider" />

          <textarea
            className="ez-invite__message"
            aria-label="Write a message (optional)"
            placeholder="Add a custom message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            data-testid="invite-message"
          />
        </div>

        {error ? (
          <p role="alert" data-testid="invite-error">
            {error}
          </p>
        ) : null}
        {result ? (
          <div role="status" data-testid="invite-result">
            <p>{result.invited.length} invited.</p>
            {result.errors.map((row) => (
              <p key={row.email}>
                {row.email}: {row.message}
              </p>
            ))}
          </div>
        ) : null}

        <div className="ez-invite__footer">
          <button
            type="button"
            className="ez-invite__secondary"
            data-testid="invite-copy-link"
            disabled={!ready}
            title="Creates a single-use invitation for the first address. Anyone with the link can join with that role."
            onClick={() => {
              setCopied(true);
              void send();
            }}
          >
            {copied ? 'Link created' : 'Copy invite link'}
          </button>
          <button
            type="button"
            className="ez-invite__primary"
            data-testid="invite-send"
            disabled={!ready}
            onClick={() => void send()}
          >
            Send invite
          </button>
        </div>
      </div>
    </div>
  );
}
